import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import LclCotizacionesColumn from './columns/LclCotizacionesColumn';
import LclEnCursoColumn from './columns/LclEnCursoColumn';
import LclFinalizadasColumn from './columns/LclFinalizadasColumn';
import LclRechazadasColumn from './columns/LclRechazadasColumn';
import { COLUMN_BUCKETS } from './columns/lclColumnActions';

/**
 * Tablero Kanban de 4 columnas para solicitudes LCL.
 *
 * Componente "slim": agrupa los datos y los reparte a las 4 columnas.
 * Cada columna es un archivo independiente (`columns/*.jsx`).
 *
 * Props:
 *  - data: array de solicitudes LCL
 *  - onAction: (actionKey, row) => void
 *  - searchTerm: término de búsqueda (filtra en cliente)
 *  - customBuckets: opcional, override de los buckets por columna
 *  - loading: indica carga en curso
 *  - boardRouteByEstado: mapa estado → ruta del tablero detallado.
 *
 * Buckets (definidos en `columns/lclColumnActions.js`):
 *  - Cotizaciones         → Borrador
 *  - Operaciones en curso → Enviada
 *  - Finalizadas          → Aprobada
 *  - Rechazadas           → Rechazada
 */

const COLUMN_COMPONENTS = {
  cotizaciones: LclCotizacionesColumn,
  'en-curso': LclEnCursoColumn,
  finalizadas: LclFinalizadasColumn,
  rechazadas: LclRechazadasColumn,
};

const matchesBucket = (row, estados) => {
  const estado = String(row?.estado || '').trim();
  if (!estado) return false;
  return estados.some((e) => e.toLowerCase() === estado.toLowerCase());
};

/**
 * Búsqueda en cliente. Los campos marítimos (puertos con UN/LOCODE,
 * tipo de servicio) reemplazan a los aeroportuarios del aéreo.
 */
const matchesSearch = (row, term) => {
  if (!term) return true;
  const t = term.toLowerCase();
  return [
    row?.codigo,
    row?._id,
    row?.clienteSnapshot?.nombre,
    row?.clienteSnapshot?.nit,
    row?.asesorSnapshot?.nombre,
    row?.tipoOperacion,
    row?.tipoServicio,
    row?.paisOrigen,
    row?.paisDestino,
    row?.ciudadOrigen,
    row?.ciudadDestino,
    row?.puertoCargaSnapshot?.nombre,
    row?.puertoCargaSnapshot?.ciudad,
    row?.puertoCargaSnapshot?.unlocode,
    row?.puertoDescargaSnapshot?.nombre,
    row?.puertoDescargaSnapshot?.ciudad,
    row?.puertoDescargaSnapshot?.unlocode,
    row?.incotermSnapshot?.codigo,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(t));
};

/**
 * Botón "Ver tablero →" del header de cada columna.
 *
 * Al hacer click captura el `getBoundingClientRect()` de la columna
 * y lo pasa en `location.state.fromRect` para que
 * `LclBoardPageTransition` anime el "wipe" desde esa posición.
 */
const BoardLink = ({ bucket, boardRouteByEstado, navigate }) => {
  const boardRoute = boardRouteByEstado ? boardRouteByEstado[bucket.estados[0]] : null;
  if (!boardRoute) return null;
  const handleClick = (e) => {
    e.stopPropagation();
    const colEl = e.currentTarget.closest('[data-column-id]');
    const rect = colEl ? colEl.getBoundingClientRect() : null;
    navigate(boardRoute, {
      state: rect
        ? {
            fromRect: {
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            },
          }
        : null,
    });
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition"
      title={`Ir al tablero de ${bucket.title}`}
    >
      Ver tablero
      <ArrowRight className="h-3 w-3" />
    </button>
  );
};

const LclOperationKanban = ({
  data = [],
  onAction,
  searchTerm = '',
  customBuckets,
  loading = false,
  boardRouteByEstado,
}) => {
  const navigate = useNavigate();
  const buckets = customBuckets || COLUMN_BUCKETS;

  const grouped = useMemo(() => {
    const out = {};
    for (const b of buckets) {
      out[b.id] = data.filter(
        (row) => matchesBucket(row, b.estados) && matchesSearch(row, searchTerm),
      );
    }
    return out;
  }, [data, buckets, searchTerm]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {buckets.map((bucket) => {
        const items = grouped[bucket.id] || [];
        const ColumnComponent = COLUMN_COMPONENTS[bucket.id];
        if (!ColumnComponent) return null;
        return (
          <ColumnComponent
            key={bucket.id}
            items={items}
            onAction={onAction}
            loading={loading}
            actionsMode="view"
            headerExtra={
              <BoardLink
                bucket={bucket}
                boardRouteByEstado={boardRouteByEstado}
                navigate={navigate}
              />
            }
          />
        );
      })}
    </div>
  );
};

export default LclOperationKanban;
